import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TeamService from "../../service/TeamService";

import { Dialog, DialogContent, Skeleton, Typography } from "@mui/material";
import { Box, Button, FlexColBox, Icon, Input } from "../../common/components";
import DialogTitleWithCloseButton from "../../common/components/Dialog/DialogTitleWithCloseButton/DialogTitleWithCloseButton";
import { useSelector } from "../../common/hooks/useSelector";
import { Teammate } from "../../containers/Team/Teammate";
import { createStyles, palette, spacing } from "../../styles/theme";
import { canDeleteUser, isCurrentUser } from "../../utils/teamUtils";
import { useThemeBreakpoints } from "../../utils/themeUtils";
import { ProjectWithDesigns } from "../../types";
import { useAuth } from "../../common/hooks/useAuth";
import AddCollaboratorControls from "./AddCollaboratorControls";
import { useTeam } from "../../context/Team";

const styles = createStyles({
  input: {
    background: palette.black.readonlyBg,
    pointerEvents: "none",
    "&>input": {
      color: `${palette.black.readonlyText} !important`,
    },
  },
});

interface Props {
  open: boolean;
  onClose: () => void;
  teamId?: string;
  projects?: ProjectWithDesigns[];
}

const InviteTeammateModal = ({ open, onClose, teamId, projects }: Props) => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const { isMobile } = useThemeBreakpoints();
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const origin = window.location.origin;
  const [token, setToken] = useState<{
    value: string | null;
    teamId: string | null;
  }>({
    value: null,
    teamId: null,
  });

  const userInfo = useSelector((state) => state.userInfo);
  const { team } = useTeam();

  useEffect(() => {
    const updateToken = async () => {
      if (!teamId) {
        //Can't get a token without the teamId
        return;
      }

      if (token.value && teamId === token.teamId) {
        // Token is up to date, do not update
        return;
      }
      setIsLoading(true);
      const {
        data: { data: newToken },
      } = await TeamService.getInviteLink(teamId);

      setToken({ value: newToken, teamId });
      setIsLoading(false);
    };

    open && updateToken();
  }, [open, teamId, token]);

  const link = `${origin}/join/${token.value}`;

  const onCopy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 5000);
  };

  const projectsList = projects?.slice(0, 3).map((project, index) => {
    const name =
      index === 2 && projects?.length > 3
        ? t("invite.last_project", {
            projectName: project.name,
            count: projects?.length - 3,
          })
        : project.name;
    return (
      <Box key={index} display={"flex"} alignItems={"center"} gap={"8px"}>
        <Icon icon="circle" size={4} color={palette.grey.primary} filled />
        <Typography variant="textRegular" color={palette.grey.primary}>
          {name}
        </Typography>
      </Box>
    );
  });

  useEffect(() => {
    if (open) {
      setCopied(false);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={true}
      fullScreen={isMobile}
      maxWidth={"md"}
    >
      <DialogTitleWithCloseButton
        id="customized-dialog-title"
        onClose={onClose}
      >
        {t("invite.invite_teammate")}
      </DialogTitleWithCloseButton>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: spacing.regularMobile, lg: spacing.regularDesktop },
        }}
      >
        <FlexColBox gap={"8px"}>
          <Typography color={palette.grey.primary} fontSize={16}>
            {t("invite.invite_description")}
          </Typography>
          <FlexColBox gap={"4px"} pl={"8px"}>
            {projectsList}
          </FlexColBox>
        </FlexColBox>
        <FlexColBox gap={"12px"}>
          <Box
            display="flex"
            gap={{ xs: spacing.regularMobile, lg: spacing.ctaSpacingDesktop }}
            alignItems="center"
            flexDirection={{ xs: "column", lg: "row" }}
            fullWidth={isMobile}
          >
            {isLoading ? (
              <Skeleton variant="rectangular" width="100%" height={36} />
            ) : (
              <>
                <Input
                  name="inviteTeamMateInput"
                  value={link}
                  readOnly
                  sx={styles.input}
                  containerSx={{
                    maxWidth: "unset",
                    flexGrow: 1,
                    width: { xs: "100%", lg: "unset" },
                  }}
                />
                <Button
                  onClick={onCopy}
                  size={isMobile ? "large" : "small"}
                  fullWidth={isMobile}
                  data-test="inviteTeammateModal.copyButton"
                  startIcon={
                    <Icon
                      icon={copied ? "done" : "content_copy"}
                      size={24}
                      color="white"
                    />
                  }
                >
                  <Typography variant="actionBigIcon" color="white">
                    {copied ? t("invite.copied") : t("invite.copy")}
                  </Typography>
                </Button>
              </>
            )}
          </Box>
          {isAdmin && <AddCollaboratorControls />}
        </FlexColBox>
        {isMobile && (
          <Box>
            <Typography variant="h2" mb={spacing.groupingMobile} mt={1}>
              {t("project.team")}
            </Typography>
            <Box
              gap={spacing.mediumMobile}
              display="flex"
              flexDirection="column"
            >
              {team?.teammates.map((teammate) => (
                <Teammate
                  userId={teammate.id}
                  first_name={teammate.firstName}
                  last_name={teammate.lastName}
                  email={teammate.email}
                  canDelete={canDeleteUser(team)}
                  key={teammate.id}
                  teamId={team.id}
                  teammateUserId={teammate.id}
                  imageUrl={teammate.imageUrl}
                  isInternalTeamMember={teammate.isInternalTeamMember}
                  isCurrentUser={isCurrentUser(userInfo, teammate.id)}
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InviteTeammateModal;
